/**
 * Training Storage for HumanReplica.ai Daily Rounds Console
 * 
 * LocalStorage persistence layer for training events and session state.
 * Handles daily training tracking and history retrieval.
 */

import type {
    TrainingEvent,
    TrainingCase,
    DraftResult,
    TrainingHistoryItem,
    DemoScenario
} from '../types/trainingTypes';
import { surpriseCalculator } from './surprise';
import type { SurpriseMetrics } from './surprise';

// ============================================
// STORAGE KEYS
// ============================================

const STORAGE_KEYS = {
    TRAINING_EVENTS: 'humanreplica_training_events',
    CURRENT_CASE: 'humanreplica_current_case',
    LAST_TRAINING_DATE: 'humanreplica_last_training_date',
    EXAM_SET: 'humanreplica_exam_set',
} as const;

// ============================================
// EVENT PERSISTENCE
// ============================================

/**
 * Get all training events from localStorage
 */
export function getTrainingEvents(): TrainingEvent[] {
    try {
        const data = localStorage.getItem(STORAGE_KEYS.TRAINING_EVENTS);
        return data ? JSON.parse(data) : [];
    } catch {
        console.error('Failed to parse training events');
        return [];
    }
}

/**
 * Save a new training event to localStorage
 */
export function saveTrainingEvent(event: TrainingEvent): void {
    try {
        const events = getTrainingEvents();
        events.unshift(event); // Add to beginning (most recent first)

        // Keep only last 30 days of events
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 30);
        const filteredEvents = events.filter(e =>
            new Date(e.timestamp) > cutoffDate
        );

        localStorage.setItem(STORAGE_KEYS.TRAINING_EVENTS, JSON.stringify(filteredEvents));

        // Update last training date
        localStorage.setItem(STORAGE_KEYS.LAST_TRAINING_DATE, new Date().toISOString().split('T')[0]);
    } catch (error) {
        console.error('Failed to save training event', error);
    }
}

// ============================================
// DAILY TRAINING TRACKING
// ============================================

/**
 * Check if user has already trained today
 */
export function hasTrainedToday(): boolean {
    try {
        const lastDate = localStorage.getItem(STORAGE_KEYS.LAST_TRAINING_DATE);
        if (!lastDate) return false;

        const today = new Date().toISOString().split('T')[0];
        return lastDate === today;
    } catch {
        return false;
    }
}

/**
 * Get today's training event if exists
 */
export function getTodaysEvent(): TrainingEvent | null {
    const events = getTrainingEvents();
    const today = new Date().toISOString().split('T')[0];

    return events.find(e =>
        e.timestamp.startsWith(today)
    ) || null;
}

// ============================================
// HISTORY RETRIEVAL
// ============================================

/**
 * Get training events from the last 7 days
 */
export function getLast7DaysEvents(): TrainingEvent[] {
    const events = getTrainingEvents();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 7);

    return events.filter(e =>
        new Date(e.timestamp) > cutoffDate
    );
}

/**
 * Get formatted history items for display
 */
export function getTrainingHistory(): TrainingHistoryItem[] {
    const events = getLast7DaysEvents();

    return events.map(event => ({
        date: formatDateForHistory(event.timestamp),
        action: event.action,
        representationScore: event.evaluation.representationQuality,
        caseTitle: event.caseTitle
    }));
}

/**
 * Format date for history display (e.g., "Jan 2")
 */
function formatDateForHistory(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ============================================
// EXAM SET MANAGEMENT
// ============================================

/**
 * Add a case to the exam set for future validation
 */
export function addToExamSet(caseId: string): void {
    try {
        const examSet = getExamSet();
        if (!examSet.includes(caseId)) {
            examSet.push(caseId);
            localStorage.setItem(STORAGE_KEYS.EXAM_SET, JSON.stringify(examSet));
        }
    } catch (error) {
        console.error('Failed to add to exam set', error);
    }
}

/**
 * Get all case IDs in the exam set
 */
export function getExamSet(): string[] {
    try {
        const data = localStorage.getItem(STORAGE_KEYS.EXAM_SET);
        return data ? JSON.parse(data) : [];
    } catch {
        return [];
    }
}

// ============================================
// DATA MANAGEMENT
// ============================================

/**
 * Clear all training data (for testing/reset)
 */
export function clearTrainingData(): void {
    localStorage.removeItem(STORAGE_KEYS.TRAINING_EVENTS);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_CASE);
    localStorage.removeItem(STORAGE_KEYS.LAST_TRAINING_DATE);
    localStorage.removeItem(STORAGE_KEYS.EXAM_SET);
}

// ============================================
// DEMO DATA GENERATION
// ============================================

const DEMO_SCENARIOS: DemoScenario[] = [
    {
        title: 'Customer Refund Request',
        prompt: 'A long-time customer is requesting a refund for a product purchased 45 days ago, outside our 30-day return window. They claim the product was defective. Draft a response.',
        baselineResponse: `Dear Customer,

Thank you for reaching out. I understand you're requesting a refund for your recent purchase.

Unfortunately, our return policy states that all returns must be made within 30 days of purchase. Since your purchase was made 45 days ago, we are unable to process a refund at this time.

If you have any other questions, please don't hesitate to reach out.

Best regards,
Customer Service`,
        replicaResponse: `Dear [Customer Name],

Thank you for bringing this to our attention. I understand the frustration when a product doesn't meet expectations, and I appreciate you being a loyal customer.

While our standard return window is 30 days, I'd like to help resolve this situation. Given the defect claim and your history with us, I'm flagging this for manager review to explore options:

- Possible store credit
- Replacement product
- Extended warranty coverage

I'll follow up within 24 hours with a resolution. In the meantime, could you please share photos of the defect so we can document this properly?

Thank you for your patience.

Best regards`,
        baselineIssues: [
            'Tone too rigid for loyal customer',
            'No acknowledgment of defect claim',
            'Missing escalation path',
            'No follow-up commitment'
        ],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Refund outside policy window — requires manager approval'
    },
    {
        title: 'Urgent Project Timeline Query',
        prompt: 'The client is asking if we can deliver the project 2 weeks earlier than agreed. The team is already at capacity. Draft a response.',
        baselineResponse: `Hi,

Thanks for your message. Moving the deadline up by 2 weeks would be challenging given our current workload. We'd need to discuss this further with the team.

Let me know if you want to schedule a call.

Thanks`,
        replicaResponse: `Hi [Client Name],

Thank you for reaching out about the timeline. I understand the urgency and want to help find a solution.

After reviewing our current capacity, here are the options I can present:

**Option A: Phased Delivery**
Deliver core functionality by your requested date, with remaining features following the original timeline.

**Option B: Scope Adjustment**
Prioritize critical features for early delivery; defer non-essential items to phase 2.

**Option C: Resource Discussion**
If full acceleration is essential, we can discuss additional resource allocation (subject to cost adjustment).

I'll need to flag this for project lead review before committing. Could we schedule a 20-minute call this week to align on priorities?

Best regards`,
        baselineIssues: [
            'Vague response without concrete options',
            'No structured alternatives presented',
            'Missing commitment to follow up',
            'Informal tone for business communication'
        ],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Timeline change requires project lead approval'
    },
    {
        title: 'Employee Performance Feedback',
        prompt: 'Draft feedback for a team member who has been missing deadlines but shows great creativity. This is for a quarterly review.',
        baselineResponse: `Performance Review

The employee has shown creativity in their work. However, there have been issues with meeting deadlines that need to be addressed.

Areas for improvement:
- Time management
- Meeting deadlines

Overall, performance has been mixed this quarter.`,
        replicaResponse: `Quarterly Performance Review

**Strengths Demonstrated:**
Your creative approach to [specific project] brought innovative solutions that the team valued. The [specific example] showed strong problem-solving skills and out-of-the-box thinking.

**Area Requiring Attention:**
Deadline adherence has been inconsistent this quarter. Specifically:
- [Project A]: 3 days late
- [Project B]: 5 days late

This impacts team coordination and client commitments.

**Development Plan:**
1. Weekly check-ins to review task progress
2. Break large deliverables into smaller milestones
3. Flag blockers early — no penalty for asking for help

**Support Offered:**
I'm committed to helping you succeed. Let's schedule bi-weekly 1:1s to address challenges proactively.

Next review checkpoint: [Date]`,
        baselineIssues: [
            'Missing specific examples',
            'No actionable development plan',
            'Tone too impersonal for sensitive feedback',
            'No support commitment offered'
        ],
        replicaIssues: [],
        guardianTriggered: true,
        escalationReason: 'Performance documentation — HR review recommended'
    }
];

/**
 * Get a demo scenario for today's case, selected using Titans surprise-based learning.
 * Prioritizes scenarios that are most surprising relative to training history.
 */
export function getDemoScenario(): DemoScenario {
    const history = getTrainingEvents();

    // Use surprise-weighted selection for intelligent case picking
    const { scenario, surprise } = surpriseCalculator.selectBySurprise(
        DEMO_SCENARIOS,
        history
    );

    return { ...scenario, surprise };
}

/**
 * Get the last calculated surprise metrics (for UI display)
 */
export function getLastSurpriseMetrics(): SurpriseMetrics | null {
    const scenario = getDemoScenario();
    return scenario.surprise || null;
}

/**
 * Generate a TrainingCase from demo scenario
 */
export function generateDemoCase(): TrainingCase {
    const scenario = getDemoScenario();
    const id = `case_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
        id,
        title: scenario.title,
        prompt: scenario.prompt,
        source: 'quick_case',
        contextPack: {
            toneGuide: true,
            policies: true,
            pastExamples: true,
            redZones: true
        },
        createdAt: new Date().toISOString()
    };
}

/**
 * Generate DraftResults from demo scenario
 */
export function generateDemoDrafts(scenario: DemoScenario): { baseline: DraftResult; replica: DraftResult } {
    return {
        baseline: {
            text: scenario.baselineResponse,
            issuesDetected: scenario.baselineIssues,
            estimatedEdits: scenario.baselineIssues.length * 2 + 3,
            estimatedTimeSeconds: scenario.baselineIssues.length * 45 + 60,
            guardianSignals: {
                requiresApproval: false,
                escalationTriggered: false
            }
        },
        replica: {
            text: scenario.replicaResponse,
            issuesDetected: scenario.replicaIssues,
            estimatedEdits: scenario.replicaIssues.length,
            estimatedTimeSeconds: scenario.replicaIssues.length * 30,
            guardianSignals: {
                requiresApproval: true,
                escalationTriggered: scenario.guardianTriggered,
                escalationReason: scenario.escalationReason,
                // Titans surprise metrics
                surpriseScore: scenario.surprise?.score,
                confidenceLevel: scenario.surprise?.confidence,
            }
        }
    };
}

/**
 * Generate unique ID
 */
export function generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================
// STATISTICS
// ============================================

/**
 * Calculate aggregate statistics from training history
 */
export function getTrainingStats(): {
    totalSessions: number;
    avgRepresentationScore: number;
    totalTimeSaved: number;
    approvalRate: number;
} {
    const events = getTrainingEvents();

    if (events.length === 0) {
        return {
            totalSessions: 0,
            avgRepresentationScore: 0,
            totalTimeSaved: 0,
            approvalRate: 0
        };
    }

    const totalSessions = events.length;
    const avgRepresentationScore = Math.round(
        events.reduce((sum, e) => sum + e.evaluation.representationQuality, 0) / totalSessions
    );
    const totalTimeSaved = events.reduce((sum, e) => sum + e.evaluation.savingsSeconds, 0);
    const approvals = events.filter(e => e.action === 'approve').length;
    const approvalRate = Math.round((approvals / totalSessions) * 100);

    return {
        totalSessions,
        avgRepresentationScore,
        totalTimeSaved,
        approvalRate
    };
}
